/**
 * Network failure capture.
 * Intercepts fetch and XMLHttpRequest to capture failed requests (status >= 400).
 * PII is scrubbed from URLs and response bodies before storage.
 */

import type { NetworkFailure } from '../types';
import { scrubPII } from '../pii';

const DEFAULT_MAX_FAILURES = 50;
const BODY_PREVIEW_MAX_LENGTH = 500;

export class NetworkCapture {
  private failures: NetworkFailure[] = [];
  private maxFailures: number;
  private installed = false;
  private originalFetch: typeof fetch | null = null;
  private originalXHROpen: typeof XMLHttpRequest.prototype.open | null = null;
  private originalXHRSend: typeof XMLHttpRequest.prototype.send | null = null;

  constructor(maxFailures: number = DEFAULT_MAX_FAILURES) {
    this.maxFailures = maxFailures;
  }

  /** Install network capture interceptors */
  install(): void {
    if (this.installed) return;
    if (typeof window === 'undefined') return;

    this.installFetchInterceptor();
    this.installXHRInterceptor();
    this.installed = true;
  }

  /** Uninstall network capture interceptors */
  uninstall(): void {
    if (!this.installed) return;

    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }

    if (this.originalXHROpen) {
      XMLHttpRequest.prototype.open = this.originalXHROpen;
      this.originalXHROpen = null;
    }

    if (this.originalXHRSend) {
      XMLHttpRequest.prototype.send = this.originalXHRSend;
      this.originalXHRSend = null;
    }

    this.installed = false;
  }

  private installFetchInterceptor(): void {
    if (typeof fetch === 'undefined') return;

    this.originalFetch = window.fetch.bind(window);
    const captureRef = this;

    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const response = await captureRef.originalFetch!(input, init);

      if (response.status >= 400) {
        const url = typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;

        let bodyPreview: string | null = null;
        try {
          const cloned = response.clone();
          const text = await cloned.text();
          bodyPreview = text.slice(0, BODY_PREVIEW_MAX_LENGTH);
        } catch {
          bodyPreview = null;
        }

        captureRef.recordFailure({
          url: scrubPII(url),
          method: init?.method ?? 'GET',
          status: response.status,
          statusText: response.statusText,
          bodyPreview: bodyPreview ? scrubPII(bodyPreview) : null,
          timestamp: new Date().toISOString(),
        });
      }

      return response;
    };
  }

  private installXHRInterceptor(): void {
    if (typeof XMLHttpRequest === 'undefined') return;

    this.originalXHROpen = XMLHttpRequest.prototype.open;
    this.originalXHRSend = XMLHttpRequest.prototype.send;
    const captureRef = this;

    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ) {
      (this as XMLHttpRequest & { _novaMethod: string; _novaUrl: string })._novaMethod = method;
      (this as XMLHttpRequest & { _novaUrl: string })._novaUrl = typeof url === 'string' ? url : url.toString();
      captureRef.originalXHROpen!.call(this, method, url, async ?? true, username ?? null, password ?? null);
    };

    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      this.addEventListener('loadend', function () {
        if (this.status >= 400) {
          const xhrWithMeta = this as XMLHttpRequest & { _novaMethod: string; _novaUrl: string };
          let bodyPreview: string | null = null;
          try {
            bodyPreview = typeof this.responseText === 'string'
              ? this.responseText.slice(0, BODY_PREVIEW_MAX_LENGTH)
              : null;
          } catch {
            bodyPreview = null;
          }

          captureRef.recordFailure({
            url: scrubPII(xhrWithMeta._novaUrl ?? 'unknown'),
            method: xhrWithMeta._novaMethod ?? 'UNKNOWN',
            status: this.status,
            statusText: this.statusText,
            bodyPreview: bodyPreview ? scrubPII(bodyPreview) : null,
            timestamp: new Date().toISOString(),
          });
        }
      });

      captureRef.originalXHRSend!.call(this, body);
    };
  }

  private recordFailure(failure: NetworkFailure): void {
    if (this.failures.length >= this.maxFailures) {
      this.failures.shift();
    }
    this.failures.push(failure);
  }

  /** Get recent network failures */
  getEntries(): NetworkFailure[] {
    return [...this.failures];
  }

  /** Get the last N failures */
  getRecent(count: number): NetworkFailure[] {
    return this.failures.slice(-count);
  }

  /** Clear all captured failures */
  clear(): void {
    this.failures = [];
  }
}
